/**
 * 全屏拖拽/点击区——无 taskId 时展示，用户可拖入或点击选择 PLY（Y-up）文件加载。
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { useViewerStore } from '@stores/viewerStore';
import type { RendererController } from '@renderer/index';
import type { SceneConfig } from '@/types/scene';

/** PLY 导入时若当前为 WebGPU，需先切到 WebGL 再加载 */
export type ReinitForPly = () => Promise<void>;

const ACCEPTED_EXTENSIONS = ['.ply', '.compressed.ply'];
const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.join(',');

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function detectFormat(): SceneConfig['asset']['format'] {
  return 'splat';
}

interface FileDropZoneProps {
  getController: () => RendererController | null;
  /** 当前 deviceType */
  deviceType: 'webgl' | 'webgpu';
  /** PLY 导入前若需要切换到 WebGL，调用此函数重新初始化 renderer */
  reinitForPly?: () => Promise<void>;
}

export function FileDropZone({ getController, deviceType, reinitForPly }: FileDropZoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // 防止并发拖拽：加载中时忽略新文件
  const loadingRef = useRef(false);
  // 记录上一次 objectUrl，加载新文件前释放
  const prevUrlRef = useRef<string | null>(null);
  // 持有当前 simTimer，组件卸载时清理
  const simTimerRef = useRef<number | null>(null);
  // ready 后延迟切 status 的定时器，让 LoadingOverlay 动画播完
  const readyTimerRef = useRef<number | null>(null);

  // 组件卸载时清除定时器，避免在已卸载组件上更新 store
  useEffect(() => {
    return () => {
      if (simTimerRef.current !== null) {
        window.clearInterval(simTimerRef.current);
        simTimerRef.current = null;
      }
      if (readyTimerRef.current !== null) {
        window.clearTimeout(readyTimerRef.current);
        readyTimerRef.current = null;
      }
    };
  }, []);

  const sceneConfig = useViewerStore((s) => s.sceneConfig);
  const status = useViewerStore((s) => s.status);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const loadFile = useCallback(
    async (file: File) => {
      if (loadingRef.current) return;
      const ctrl = getController();
      if (!ctrl) return;

      // PLY 在 WebGPU 下存在排序 bug，导入前切换到 WebGL
      if (deviceType === 'webgpu' && reinitForPly) {
        await reinitForPly();
      }

      loadingRef.current = true;
      const { setStatus, setLoadProgress, setSceneConfig } = useViewerStore.getState();
      setStatus('loading');
      setLoadProgress(0);

      // 释放上一次加载的 objectUrl，避免内存泄漏
      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = null;
      }

      const objectUrl = URL.createObjectURL(file);
      prevUrlRef.current = objectUrl;

      // blob URL 无网络下载，PlayCanvas asset 'progress' 事件不会触发，
      // 用渐进式模拟进度给用户加载反馈，封顶 90% 留给 ready。
      // 模拟进度也需遵守单调递增，避免覆盖真实 progress 事件写入的更高值。
      let simProgress = 0;
      simTimerRef.current = window.setInterval(() => {
        // 越接近 90% 增速越慢，避免过早满格
        simProgress += (90 - simProgress) * 0.08;
        if (simProgress > 1) {
          const target = Math.min(Math.round(simProgress), 90);
          const current = useViewerStore.getState().loadProgress ?? 0;
          if (target > current) setLoadProgress(target);
        }
      }, 200);

      const cleanup = () => {
        ctrl.off('ready', onReady);
        ctrl.off('error', onError);
        if (simTimerRef.current !== null) {
          window.clearInterval(simTimerRef.current);
          simTimerRef.current = null;
        }
        if (readyTimerRef.current !== null) {
          window.clearTimeout(readyTimerRef.current);
          readyTimerRef.current = null;
        }
      };

      const onReady = () => {
        cleanup();
        // 从当前进度快速补齐到 100，避免 simTimer 还在低位时直接跳 100 造成闪烁
        const startProgress = useViewerStore.getState().loadProgress ?? 0;
        const rampDuration = 600;
        const rampStart = performance.now();
        const rampTick = () => {
          const now = performance.now();
          const t = Math.min((now - rampStart) / rampDuration, 1);
          const eased = 1 - Math.pow(1 - t, 2);
          const val = Math.round(startProgress + (100 - startProgress) * eased);
          setLoadProgress(val);
          if (t < 1) {
            readyTimerRef.current = window.setTimeout(rampTick, 16);
          } else {
            setLoadProgress(100);
            // 补齐动画播完后再延迟切 ready，让用户看到 100%
            readyTimerRef.current = window.setTimeout(() => {
              readyTimerRef.current = null;
              loadingRef.current = false;
              setStatus('ready');
              setSceneConfig({
                id: 'dropped-file',
                name: file.name,
                asset: {
                  id: 'dropped-file',
                  url: objectUrl,
                  format: detectFormat(),
                },
                initialCamera: {
                  position: { x: 0, y: 1.5, z: 4 },
                  target: { x: 0, y: 0, z: 0 },
                },
                hotspots: [],
              });
            }, 200);
          }
        };
        readyTimerRef.current = window.setTimeout(rampTick, 16);
      };

      // blob URL 的下载进度是本地读取，触发极快且无 UX 价值，
      // 完全由 simTimer 驱动进度，onReady 负责收尾 100%。
      // 不注册 onProgress，避免下载进度盖过模拟动画。

      const onError = (err: unknown) => {
        cleanup();
        loadingRef.current = false;
        URL.revokeObjectURL(objectUrl);
        prevUrlRef.current = null;
        setStatus('error', err instanceof Error ? err.message : 'Failed to load file');
      };

      ctrl.on('ready', onReady);
      ctrl.on('error', onError);

      void ctrl
        .loadScene({
          url: objectUrl,
          filename: file.name,
          skipDefaultCollision: true,
          skipDefaultTrack: true,
        })
        .catch((err) => {
          onError(err);
        });
    },
    [getController, deviceType, reinitForPly],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      dragCounterRef.current = 0;

      const files = Array.from(e.dataTransfer.files).filter(isAcceptedFile);
      if (files.length === 0) return;
      void loadFile(files[0]);
    },
    [loadFile],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && isAcceptedFile(file)) void loadFile(file);
      // 重置 value 允许重复选择同一文件
      e.target.value = '';
    },
    [loadFile],
  );

  // 无场景配置且不在加载中时显示（idle = 初始无场景源，ready = renderer 已就绪）
  if (sceneConfig !== null || (status !== 'ready' && status !== 'idle')) return null;

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      className="pointer-events-auto absolute inset-0 z-30 flex cursor-pointer items-center justify-center"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        onChange={handleFileChange}
        className="hidden"
      />
      {dragOver && (
        <div className="absolute inset-4 rounded-2xl border-2 border-dashed border-white/40 bg-slate-950/60 backdrop-blur-sm" />
      )}
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="rounded-2xl border-2 border-dashed border-white/25 px-12 py-10 text-white/60 transition-colors hover:border-white/40 hover:text-white/80">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mx-auto mb-4 size-12"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <p className="text-lg font-medium text-white/80">拖拽或点击选择场景文件.ply（Y-up）</p>
        </div>
      </div>
    </div>
  );
}
